import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {InspectionTypeSimpleResponse} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {ActiveInactivePills} from 'src/design/atoms/pillPresets';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {ActiveStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {
  useDeleteInspectionTypeMutation,
  useGetInspectionTypeListV2Query,
} from 'src/redux/queries/inspectionType';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const InspectionTypeTable: React.FC = () => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const inspectionPerms = useInspectionPermissions();
  const [deleteTarget, setDeleteTarget] =
    useState<InspectionTypeSimpleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteInspectionType] = useDeleteInspectionTypeMutation();

  const columns = useMemo<MRT_ColumnDef<InspectionTypeSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'inspection-type-v2',
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'active',
        value: ['true'],
      },
    ],
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetInspectionTypeListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleDelete = async () => {
    if (!deleteTarget) return;

    confirmDeleteHandlers.close();
    setIsDeleting(true);

    try {
      await deleteInspectionType(deleteTarget.id).unwrap();
      setAlert(
        `${deleteTarget.name ?? 'Inspection Type'} was successfully deleted.`,
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeleting(false);
    setDeleteTarget(undefined);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() =>
              navigate(PathUtility.InspectionTypesView(row.original.id))
            }
          />
          {inspectionPerms.delete && (
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => {
                  setDeleteTarget(row.original);
                  confirmDeleteHandlers.open();
                }}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          )}
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {inspectionPerms.create && (
              <TableToolbar.Action
                variant="create"
                onClick={() => navigate(PathUtility.InspectionTypesCreate())}>
                New Inspection Type
              </TableToolbar.Action>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      <Text size="sm" c="dimmed" mb="sm">
        Manage the catalog of inspection types.
      </Text>
      {table}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title="Are you sure you want to delete this inspection type?"
        body="Equipment types already tagged with this inspection type keep the tag. Deactivate it instead if you only want to retire it from new tagging."
      />
    </>
  );
};
